// =============================================================================
// AI ASSISTANT PANEL - Main chat sidebar
// =============================================================================

import { useRef, useEffect } from 'react';
import {
  X,
  Send,
  Bot,
  Database,
  GitBranch,
  FileText,
  Loader2,
  Plus,
  Trash2,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { useAIAssistantStore } from './store';
import { AGENTS, SUGGESTED_PROMPTS, type Message } from './types';

const ICON_MAP: Record<string, LucideIcon> = {
  Database,
  GitBranch,
  FileText,
  Bot,
};

function AgentSelector() {
  const { activeAgentType, setActiveAgent, createConversation } = useAIAssistantStore();

  return (
    <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-secondary-900 dark:text-white">
          Select Agent
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => createConversation(activeAgentType)}
        >
          <Plus className="h-4 w-4 mr-1" />
          New Chat
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {AGENTS.map((agent) => {
          const Icon = ICON_MAP[agent.icon] || Bot;
          return (
            <button
              key={agent.id}
              onClick={() => setActiveAgent(agent.id)}
              className={cn(
                'flex items-center gap-2 p-2 rounded-lg border transition-all text-left',
                activeAgentType === agent.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300'
              )}
            >
              <div
                className="flex h-8 w-8 items-center justify-center rounded-md"
                style={{ backgroundColor: `${agent.color}20` }}
              >
                <Icon className="h-4 w-4" style={{ color: agent.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                  {agent.name}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  const agent = AGENTS.find((a) => a.id === message.agentType);
  const Icon = agent ? ICON_MAP[agent.icon] || Bot : Bot;

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isUser
            ? 'bg-primary-500'
            : 'bg-secondary-100 dark:bg-secondary-800'
        )}
        style={!isUser && agent ? { backgroundColor: `${agent.color}20` } : undefined}
      >
        {isUser ? (
          <span className="text-sm font-medium text-white">U</span>
        ) : (
          <Icon
            className="h-4 w-4"
            style={{ color: agent?.color || '#6B7280' }}
          />
        )}
      </div>

      {/* Message content */}
      <div
        className={cn(
          'flex-1 rounded-lg px-4 py-2.5',
          isUser
            ? 'bg-primary-500 text-white'
            : 'bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-white'
        )}
      >
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {message.content.split('\n').map((line, i) => (
            <p key={i} className="mb-2 last:mb-0">
              {line}
            </p>
          ))}
        </div>
        <span className="text-xs opacity-60 mt-1 block">
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
}

function SuggestedPrompts() {
  const { activeAgentType, sendMessage } = useAIAssistantStore();

  const relevantPrompts = SUGGESTED_PROMPTS.filter(
    (p) => p.agentType === activeAgentType || p.agentType === 'general'
  ).slice(0, 4);

  return (
    <div className="p-4 space-y-2">
      <p className="text-sm text-secondary-500">Suggested prompts:</p>
      <div className="space-y-2">
        {relevantPrompts.map((prompt, i) => (
          <button
            key={i}
            onClick={() => sendMessage(prompt.text)}
            className="w-full text-left text-sm p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors"
          >
            {prompt.text}
          </button>
        ))}
      </div>
    </div>
  );
}

function ConversationHistory() {
  const {
    conversations,
    currentConversationId,
    selectConversation,
    deleteConversation,
  } = useAIAssistantStore();

  if (conversations.length === 0) return null;

  return (
    <div className="border-t border-secondary-200 dark:border-secondary-700">
      <div className="p-4 pb-2">
        <span className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">
          Recent Conversations
        </span>
      </div>
      <div className="px-2 pb-2 space-y-1 max-h-40 overflow-y-auto">
        {conversations.slice(0, 5).map((conversation) => (
          <div
            key={conversation.id}
            className={cn(
              'flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors group',
              currentConversationId === conversation.id
                ? 'bg-primary-50 dark:bg-primary-900/20'
                : 'hover:bg-secondary-50 dark:hover:bg-secondary-800'
            )}
            onClick={() => selectConversation(conversation.id)}
          >
            <MessageSquare className="h-4 w-4 text-secondary-400" />
            <span className="flex-1 text-sm truncate text-secondary-700 dark:text-secondary-300">
              {conversation.title}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                deleteConversation(conversation.id);
              }}
            >
              <Trash2 className="h-3 w-3 text-secondary-400" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AIAssistantPanel() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    isOpen,
    isLoading,
    inputValue,
    close,
    setInputValue,
    sendMessage,
    currentMessages,
  } = useAIAssistantStore();

  const messages = currentMessages();

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus textarea when opened
  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-secondary-900 border-l border-secondary-200 dark:border-secondary-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/20">
            <Bot className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="font-semibold text-secondary-900 dark:text-white">
              AI Assistant
            </h2>
            <Badge variant="secondary" className="text-[10px]">
              Beta
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={close}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Agent selector */}
      <AgentSelector />

      {/* Messages area */}
      <ScrollArea className="flex-1">
        {messages.length === 0 ? (
          <SuggestedPrompts />
        ) : (
          <div className="p-4 space-y-4">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-800">
                  <Loader2 className="h-4 w-4 animate-spin text-secondary-500" />
                </div>
                <div className="flex-1 rounded-lg bg-secondary-100 dark:bg-secondary-800 px-4 py-2.5">
                  <span className="text-sm text-secondary-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Conversation history */}
      <ConversationHistory />

      {/* Input area */}
      <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask the AI assistant..."
            rows={2}
            className="flex-1 resize-none"
            disabled={isLoading}
          />
          <Button
            type="submit"
            size="icon"
            className="h-full"
            disabled={!inputValue.trim() || isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
